import type {
  MilestoneMap1,
  Objective1,
  Conflict1,
  MilestoneMapInterval,
} from '@mm-shared/types';

export type DependencyRecord = {
  sourceObjective: { uuid: string; text: string };
  sourceResult: { uuid: string; text: string; isKeyResult: boolean };
  sourceMap: { uuid: string; text: string };
  targetObjective: { uuid: string; text: string };
  targetResult: { uuid: string; text: string; isKeyResult: boolean };
  targetMap: { uuid: string; text: string };
  // Fields for conflict detection
  sourceMapInterval: MilestoneMapInterval;
  sourceResultDeadline: string; // date
  targetMapInterval: MilestoneMapInterval;
  targetResultDeadline: string; // date
  hasDeadlineConflict: boolean;
};

/**
 * Returns all DependencyRecords for a single objective.
 *
 * For each result in the objective, iterates over its dependencies and constructs a DependencyRecord.
 * - Looks up the target result, objective, and map using provided lists.
 * - If any target data is missing, skips the dependency (TODO: could use placeholders).
 * - Each DependencyRecord contains source/target result, objective, and map info for display.
 * - Checks for deadline conflicts (when dependency's date >= dependent's date)
 *
 * @param objective The objective whose results' dependencies to extract
 * @param allObjectives All objectives in the system (for lookup)
 * @param sourceMap The map this objective belongs to (for source info)
 * @param allMaps All maps in the system (for target lookup)
 * @returns Array of DependencyRecord objects for all dependencies under this objective
 */
export function getDependencyRecordsFromObjective(
  objective: Objective1,
  allObjectives: Objective1[],
  sourceMap?: MilestoneMap1,
  allMaps?: MilestoneMap1[],
): DependencyRecord[] {
  // Build a lookup for objectives by uuid for fast access
  const objectiveByUuid = Object.fromEntries(allObjectives.map((obj) => [obj.uuid, obj]));
  // Build a flat list of all results for fast lookup by uuid
  const allResults = allObjectives.flatMap((obj) => obj.results);
  // Build a lookup for maps by uuid (if provided)
  const mapByUuid =
    allMaps && allMaps.length > 0 && allMaps[0].uuid
      ? Object.fromEntries(allMaps.map((m) => [m.uuid, m]))
      : sourceMap
        ? { [sourceMap.uuid]: sourceMap }
        : {};

  const dependencyRecords: DependencyRecord[] = [];
  // For each result in the objective...
  for (const sourceResult of objective.results) {
    // ...iterate over its dependencies
    for (const dep of sourceResult.dependencies || []) {
      // Find the target result by uuid (may be missing if not loaded)
      const targetResult = allResults.find((r) => r.uuid === dep.targetResultUuid);
      // Find the target objective by uuid (may be missing if not loaded)
      const targetObjective = dep.targetObjectiveUuid
        ? objectiveByUuid[dep.targetObjectiveUuid]
        : undefined;
      // Find the source map (provided as argument or fallback to empty)
      const sourceMapObj = sourceMap || { uuid: '', title: '' };
      // Find the target map by uuid (may be missing if not loaded)
      const targetMapObj = dep.targetMapUuid
        ? mapByUuid[dep.targetMapUuid] || { uuid: dep.targetMapUuid, title: dep.targetMapUuid }
        : { uuid: '', title: '' };
      // Only add the dependency if both target result and objective are found
      // (TODO: could relax this to always show, with placeholders for missing data)
      if (targetResult && targetObjective) {
        // Check for deadline conflict
        const sourceDate = new Date(sourceResult.deadline || sourceResult.forIntervalEnding);
        const targetDate = new Date(targetResult.deadline || targetResult.forIntervalEnding);
        const hasDeadlineConflict = targetDate >= sourceDate;

        dependencyRecords.push({
          sourceObjective: { uuid: objective.uuid, text: objective.text },
          sourceResult: {
            uuid: sourceResult.uuid,
            text: sourceResult.text,
            isKeyResult: sourceResult.isKeyResult,
          },
          sourceMap: { uuid: sourceMapObj.uuid, text: sourceMapObj.title },
          targetObjective: { uuid: targetObjective.uuid, text: targetObjective.text },
          targetResult: {
            uuid: targetResult.uuid,
            text: targetResult.text,
            isKeyResult: targetResult.isKeyResult,
          },
          targetMap: { uuid: targetMapObj.uuid, text: targetMapObj.title },
          // New fields
          sourceMapInterval: ('interval' in sourceMapObj
            ? sourceMapObj.interval
            : 'months') as MilestoneMapInterval,
          sourceResultDeadline: sourceResult.deadline || sourceResult.forIntervalEnding,
          targetMapInterval: ('interval' in targetMapObj
            ? targetMapObj.interval
            : 'months') as MilestoneMapInterval,
          targetResultDeadline: targetResult.deadline || targetResult.forIntervalEnding,
          hasDeadlineConflict,
        });
      }
    }
  }
  return dependencyRecords;
}

/**
 * Extracts all dependencies from all results in all objectives in a given map.
 * Returns a flat array of DependencyRecord objects.
 */
export function getDependencyRecords(
  map: MilestoneMap1,
  objectives: Objective1[],
  allMaps?: MilestoneMap1[],
): DependencyRecord[] {
  if (!map || !objectives) return [];
  // Get all objectives for this map
  const mapObjectives = objectives.filter((obj) => map.objectiveUuids.includes(obj.uuid));
  // Use the new utility for each objective
  return mapObjectives.flatMap((obj) =>
    getDependencyRecordsFromObjective(obj, objectives, map, allMaps),
  );
}

/**
 * Returns all map UUIDs referenced in dependencies (source or target), excluding the current map.
 */
export function getAllRelatedMapUuids(
  currentMapUuid: string,
  dependencies: DependencyRecord[],
): Set<string> {
  const relatedUuids = new Set<string>();
  for (const dep of dependencies) {
    if (dep.sourceMap.uuid && dep.sourceMap.uuid !== currentMapUuid) {
      relatedUuids.add(dep.sourceMap.uuid);
    }
    if (dep.targetMap.uuid && dep.targetMap.uuid !== currentMapUuid) {
      relatedUuids.add(dep.targetMap.uuid);
    }
  }
  return relatedUuids;
}

/**
 * Checks for conflict changes in a single dependency relationship.
 * Returns a Conflict1 object if a conflict is created or resolved, null otherwise.
 */
export function checkDependencyForConflicts(
  beforeDep: DependencyRecord | undefined,
  afterDep: DependencyRecord | undefined,
): Conflict1 | null {
  // If there's no before record but there is an after record,
  // this is a new dependency. Check if it starts with a conflict.
  if (!beforeDep && afterDep) {
    if (afterDep.hasDeadlineConflict) {
      return {
        dateFlagged: new Date().toISOString(),
        partiesText: {
          sourceObjective: afterDep.sourceObjective.text,
          sourceResult: afterDep.sourceResult.text,
          sourceMap: afterDep.sourceMap.text,
          targetObjective: afterDep.targetObjective.text,
          targetResult: afterDep.targetResult.text,
          targetMap: afterDep.targetMap.text,
        },
        partiesUuids: {
          sourceObjective: afterDep.sourceObjective.uuid,
          sourceResult: afterDep.sourceResult.uuid,
          sourceMap: afterDep.sourceMap.uuid,
          targetObjective: afterDep.targetObjective.uuid,
          targetResult: afterDep.targetResult.uuid,
          targetMap: afterDep.targetMap.uuid,
        },
        issue: {
          sourceMapInterval: afterDep.sourceMapInterval,
          sourceResultDeadline: afterDep.sourceResultDeadline,
          targetMapInterval: afterDep.targetMapInterval,
          targetResultDeadline: afterDep.targetResultDeadline,
        },
      };
    }
    return null;
  }

  // If there's no after record or no before record, the dependency was removed or invalid
  if (!afterDep || !beforeDep) {
    return null;
  }

  // Both records exist, check for conflict changes
  const prevConflict = beforeDep.hasDeadlineConflict;
  const newConflict = afterDep.hasDeadlineConflict;

  // New conflict detected
  if (!prevConflict && newConflict) {
    return {
      dateFlagged: new Date().toISOString(),
      partiesText: {
        sourceObjective: afterDep.sourceObjective.text,
        sourceResult: afterDep.sourceResult.text,
        sourceMap: afterDep.sourceMap.text,
        targetObjective: afterDep.targetObjective.text,
        targetResult: afterDep.targetResult.text,
        targetMap: afterDep.targetMap.text,
      },
      partiesUuids: {
        sourceObjective: afterDep.sourceObjective.uuid,
        sourceResult: afterDep.sourceResult.uuid,
        sourceMap: afterDep.sourceMap.uuid,
        targetObjective: afterDep.targetObjective.uuid,
        targetResult: afterDep.targetResult.uuid,
        targetMap: afterDep.targetMap.uuid,
      },
      issue: {
        sourceMapInterval: afterDep.sourceMapInterval,
        sourceResultDeadline: afterDep.sourceResultDeadline,
        targetMapInterval: afterDep.targetMapInterval,
        targetResultDeadline: afterDep.targetResultDeadline,
      },
    };
  }
  // Conflict newly resolved
  else if (prevConflict && !newConflict) {
    return {
      dateFlagged: beforeDep.sourceResultDeadline,
      dateResolved: new Date().toISOString(),
      partiesText: {
        sourceObjective: beforeDep.sourceObjective.text,
        sourceResult: beforeDep.sourceResult.text,
        sourceMap: beforeDep.sourceMap.text,
        targetObjective: beforeDep.targetObjective.text,
        targetResult: beforeDep.targetResult.text,
        targetMap: beforeDep.targetMap.text,
      },
      partiesUuids: {
        sourceObjective: beforeDep.sourceObjective.uuid,
        sourceResult: beforeDep.sourceResult.uuid,
        sourceMap: beforeDep.sourceMap.uuid,
        targetObjective: beforeDep.targetObjective.uuid,
        targetResult: beforeDep.targetResult.uuid,
        targetMap: beforeDep.targetMap.uuid,
      },
      issue: {
        sourceMapInterval: beforeDep.sourceMapInterval,
        sourceResultDeadline: beforeDep.sourceResultDeadline,
        targetMapInterval: beforeDep.targetMapInterval,
        targetResultDeadline: beforeDep.targetResultDeadline,
      },
    };
  }

  return null;
}

/**
 * Detects if there are any changes in dependency conflicts between two sets of dependencies.
 * Returns an array of Conflict1 objects for any new conflicts or resolutions.
 */
export function detectConflictChange(
  beforeDeps: DependencyRecord[],
  afterDeps: DependencyRecord[],
): { conflicts: Conflict1[] } {
  const conflicts: Conflict1[] = [];

  // Create maps for faster lookup
  const beforeDepsMap = new Map<string, DependencyRecord>();
  const afterDepsMap = new Map<string, DependencyRecord>();

  // Helper to create a unique key for each dependency
  function getDependencyKey(dep: DependencyRecord) {
    return `${dep.sourceResult.uuid}-${dep.targetResult.uuid}`;
  }

  // Populate maps
  beforeDeps.forEach((dep) => beforeDepsMap.set(getDependencyKey(dep), dep));
  afterDeps.forEach((dep) => afterDepsMap.set(getDependencyKey(dep), dep));

  // Check all dependencies in before state
  beforeDepsMap.forEach((beforeDep, key) => {
    const afterDep = afterDepsMap.get(key);
    const conflict = checkDependencyForConflicts(beforeDep, afterDep);
    if (conflict) {
      conflicts.push(conflict);
    }
  });

  // Check for new dependencies in after state
  afterDepsMap.forEach((afterDep, key) => {
    if (!beforeDepsMap.has(key)) {
      const conflict = checkDependencyForConflicts(undefined, afterDep);
      if (conflict) {
        conflicts.push(conflict);
      }
    }
  });

  return { conflicts };
}

/**
 * Creates a consistent, human-readable edge ID from a dependency record.
 * Format: source-{uuid}--target-{uuid}
 * Using double dash (--) to separate source result UUID and target result UUID for better readability
 */
export function getEdgeIdFromDependencyRecord(dep: DependencyRecord): string {
  return `source-${dep.sourceResult.uuid}--target-${dep.targetResult.uuid}`;
}

/**
 * Wrapper for getEdgeIdFromDependencyRecord.
 *
 * TODO: Consider supporting shortID and using this more broadly
 * as needed for dependencies outside of React Flow contexts.
 */
export function getDependencyId(dep: DependencyRecord): string {
  return getEdgeIdFromDependencyRecord(dep);
}

/**
 * Parses a dependency ID to extract source and target result UUIDs.
 * Expects ID in format: source-{uuid}--target-{uuid}
 * @param dependencyId The dependency ID to parse
 * @returns Object containing source and target result UUIDs, or null if invalid format
 */
export function getIdsFromDependencyId(
  dependencyId: string,
): { sourceId: string; targetId: string } | null {
  const parts = dependencyId.split('--');
  if (parts.length !== 2) {
    console.error(
      `Invalid dependency ID format: "${dependencyId}". Expected format: "source-{uuid}--target-{uuid}"`,
    );
    return null;
  }

  const sourcePart = parts[0];
  const targetPart = parts[1];

  if (!sourcePart.startsWith('source-')) {
    console.error(
      `Invalid source part in dependency ID: "${sourcePart}". Expected format: "source-{uuid}"`,
    );
    return null;
  }

  if (!targetPart.startsWith('target-')) {
    console.error(
      `Invalid target part in dependency ID: "${targetPart}". Expected format: "target-{uuid}"`,
    );
    return null;
  }

  const sourceId = sourcePart.replace('source-', '');
  const targetId = targetPart.replace('target-', '');

  return {
    sourceId,
    targetId,
  };
}
